import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { selectAdminToken, selectOrganizationId, selectSessionId } from './sessionSlice';
import { processQuestions } from './questionSlice';

export const fetchThemeData = createAsyncThunk(
  'api/fetchThemeData',
  async (
    { themeId = null, isAdmin = false },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const adminToken = selectAdminToken(state);
      let organizationId = selectOrganizationId(state);
      let sessionId = selectSessionId(state);
      
      // For admin pages, keep the actual sessionId from Redux
      // (superadmin will have 'admin', regular admin will have their actual sessionId)
      // Only use organizationId fallback if needed
      if (isAdmin) {
        organizationId = organizationId || 'admin';
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/fetchThemeData`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            currentTheme: themeId,
            organizationId,
            sessionId,
          }),
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const persistedTheme = localStorage.getItem('currentTheme');

const themeSlice = createSlice({
  name: 'theme',
  initialState: {
    currentTheme: persistedTheme || null,
    availableThemes: [],
    status: 'idle',
    error: null,
    data: null,
  },
  reducers: {
    selectTheme: (state, action) => {
      state.currentTheme = action.payload;
      localStorage.setItem('currentTheme', action.payload);
    },
    resetTheme: (state) => {
      state.currentTheme = null;
      localStorage.removeItem('currentTheme');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemeData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchThemeData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload.data;

        const themeName = action.payload.data.themename;
        state.currentTheme = themeName;
        localStorage.setItem('currentTheme', themeName);

        if (action.meta.arg.isAdmin) {
          state.availableThemes = action.payload.availableThemes || [];
        }
      })
      .addCase(fetchThemeData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(processQuestions.rejected, (_state, action) => {
        console.error('processQuestions failed:', action.payload);
      });
  },
});

export { processQuestions };
export const { selectTheme, resetTheme } = themeSlice.actions;
export default themeSlice.reducer;
